import { Box, Button, FormControlLabel, Modal, Radio, RadioGroup, TextField, CircularProgress } from "@mui/material";
import React, { useState, useEffect } from "react";
import AddressCard from "./AddressCard";
import AddressForm from "./AddressForm";
import PricingCard from "../Cart/PricingCard";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { fetchUserProfile } from "../../../State/AuthSlice";
import { createOrder } from "../../../State/customer/orderSlice";
import { useNavigate } from "react-router-dom";
import { fetchUserAddresses } from "../../../State/customer/addressSlice";

// Modal style customization
const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 600 },
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: '90vh',
    overflowY: 'auto'
};

const paymentOptions = [
    {
        value: "PAY_ON_DELIVERY",
        label: "Pay On Delivery",
        description: "Pay via M-Pesa Till Number upon delivery."
    },
    {
        value: "MPESA",
        image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRyFJXsJE4CzUllsr4z9Uo61SvMlit2Tzkz7fl75b6aJooj4g5kpmWbAQ8&s=10",
        label: "M-Pesa Express (STK Push)",
        description: "Instant prompt to your phone via M-Pesa Till Number"
    }
];

const Checkout = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [paymentGateway, setPaymentGateway] = useState("PAY_ON_DELIVERY");
    const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
    const [mpesaPhoneNumber, setMpesaPhoneNumber] = useState("");
    const [orderSubmitted, setOrderSubmitted] = useState(false);

    const { user } = useAppSelector((state) => state.auth);
    const { cart } = useAppSelector((state) => state.cart);
    const { loading } = useAppSelector((state) => state.order);

    const addressState = useAppSelector((state: any) => state.address);
    const savedAddresses = addressState?.addresses?.length > 0
        ? addressState.addresses
        : (user?.addresses || []);

    useEffect(() => {
        const jwt = localStorage.getItem("jwt");
        if (jwt) {
            dispatch(fetchUserProfile({ jwt }));
            dispatch(fetchUserAddresses(jwt));
        }
    }, [dispatch]);

    useEffect(() => {
        if (savedAddresses.length > 0 && selectedAddressId === null) {
            setSelectedAddressId(savedAddresses[0].id);
        }
    }, [savedAddresses, selectedAddressId]);

    useEffect(() => {
        if (user?.mobile) {
            setMpesaPhoneNumber(user.mobile);
        }
    }, [user]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        const jwt = localStorage.getItem("jwt");
        if (jwt) {
            dispatch(fetchUserAddresses(jwt));
        }
    };

    const handlePaymentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPaymentGateway(event.target.value);
    };

    const handleCheckoutSubmit = async () => {
        const jwtToken = localStorage.getItem("jwt") || "";

        if (!selectedAddressId) {
            alert("Please select a delivery address before completing your order.");
            return;
        }

        if (paymentGateway === "MPESA") {
            const cleanPhone = mpesaPhoneNumber.trim();
            if (!cleanPhone || cleanPhone.length < 9) {
                alert("Please provide a valid M-Pesa phone number (e.g., 0712345678 or 254712345678)");
                return;
            }
        }

        const matchedAddress = savedAddresses.find((addr: any) => addr.id === selectedAddressId);

        if (!matchedAddress) {
            alert("Selected address details could not be found.");
            return;
        }

        const res = await dispatch(createOrder({
            address: matchedAddress,
            jwt: jwtToken,
            paymentGateway: paymentGateway,
            phoneNumber: paymentGateway === "MPESA" ? mpesaPhoneNumber : undefined
        }));

        if (createOrder.fulfilled.match(res)) {
            setOrderSubmitted(true);
        }
    };

    if (orderSubmitted && !cart) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-5">
                <div className="bg-green-50 border border-green-200 rounded-full p-4 mb-4">
                    <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-950 mb-2">Order placed successfully!</h1>
                <p className="text-gray-500 max-w-sm mb-6">
                    {paymentGateway === "MPESA"
                        ? "Please check your phone and enter your PIN on the M-Pesa prompt to complete payment."
                        : "Your order has been placed. Please pay via our store Till Number upon delivery."}
                </p>
                <Button variant="contained" color="primary" onClick={() => navigate("/")}>
                    Continue Shopping
                </Button>
            </div>
        );
    }

    return (
        <div className='pt-10 px-5 sm:px-10 md:px-44 lg:px-60 min-h-screen'>
            <div className='space-y-5 lg:space-y-0 lg:grid grid-cols-3 lg:gap-9'>
                {/* Left Column: Address Selection */}
                <div className='col-span-2 space-y-5'>
                    <div className='flex justify-between items-center'>
                        <h1 className='font-semibold text-lg'>Select Address</h1>
                        <Button variant="outlined" onClick={handleOpen}>
                            Add new Address
                        </Button>
                    </div>

                    <div className='text-xs font-medium space-y-3'>
                        <p className="text-gray-500 uppercase tracking-wider text-[11px]">Saved Addresses</p>
                        <div className='space-y-3 text-sm'>
                            {savedAddresses.length > 0 ? (
                                savedAddresses.map((addr: any) => (
                                    <AddressCard
                                        key={addr.id}
                                        address={addr}
                                        selectedValue={selectedAddressId}
                                        onChange={setSelectedAddressId}
                                    />
                                ))
                            ) : (
                                <p className="text-gray-400 italic">No saved addresses found. Please add a new address.</p>
                            )}
                        </div>
                    </div>

                    <div className='py-4 px-5 rounded-md border border-dashed flex justify-center items-center bg-gray-50/50'>
                        <Button startIcon={<span>+</span>} onClick={handleOpen}>
                            Add new Address
                        </Button>
                    </div>
                </div>

                {/* Right Column: Payment & Summary Info */}
                <div>
                    <div className='border rounded-md bg-white shadow-sm overflow-hidden'>
                        <div className="space-y-3 p-5 border-b bg-gray-50/30">
                            <h1 className="text-gray-800 font-medium pb-1 text-center">Choose Payment Method</h1>

                            <RadioGroup
                                name="payment-method-group"
                                className="flex flex-col gap-2"
                                onChange={handlePaymentChange}
                                value={paymentGateway}
                            >
                                {paymentOptions.map((item) => (
                                    <div
                                        key={item.value}
                                        className={`border p-3 rounded-md transition-all cursor-pointer ${paymentGateway === item.value ? "border-green-600 bg-green-50/20" : "border-gray-200"
                                            }`}
                                    >
                                        <FormControlLabel
                                            className="w-full m-0"
                                            value={item.value}
                                            control={<Radio size="small" color="success" />}
                                            label={
                                                <div className="flex items-center gap-3">
                                                    {item.image ? (
                                                        <img className="w-12 h-6 object-contain" src={item.image} alt={item.label} />
                                                    ) : null}
                                                    <div>
                                                        <p className="font-semibold text-sm text-gray-800">{item.label}</p>
                                                        {item.description && (
                                                            <p className="text-xs text-gray-500">{item.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            }
                                        />

                                        {paymentGateway === "MPESA" && item.value === "MPESA" && (
                                            <div className="mt-3 pt-3 border-t border-gray-200">
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="M-Pesa Phone Number"
                                                    placeholder="e.g. 0712345678"
                                                    value={mpesaPhoneNumber}
                                                    onChange={(e) => setMpesaPhoneNumber(e.target.value)}
                                                    helperText="You will receive an STK prompt on this phone"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </RadioGroup>
                        </div>

                        <PricingCard />

                        <div className='p-5'>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                disabled={loading}
                                onClick={handleCheckoutSubmit}
                                sx={{ py: "11px", textTransform: "none", fontWeight: 600 }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : paymentGateway === "MPESA" ? (
                                    "Pay with M-Pesa"
                                ) : (
                                    "Place Order (Pay on Delivery)"
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal for Address Form */}
            <Modal open={open} onClose={handleClose}>
                <Box sx={modalStyle}>
                    <AddressForm handleClose={handleClose} />
                </Box>
            </Modal>
        </div>
    );
};

export default Checkout;